"use client";

import { useState } from 'react';
import { FiCopy, FiCheck } from 'react-icons/fi';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  quoteUrl: string;
}

export function ShareModal({ isOpen, onClose, quoteUrl }: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(quoteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-25 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h3 className="text-xl font-medium text-gray-900 mb-2">
            Share Your Holiday Quote
          </h3>
          
          <p className="text-gray-600 text-sm mb-4">
            Share this holiday package quote with friends and family! They'll be able to view all the details and pricing information.
          </p>
          
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={quoteUrl}
              className="flex-1 p-2 border rounded-lg bg-gray-50 text-sm"
              aria-label="Quote URL"
            />
            <button
              onClick={handleCopy}
              className="inline-flex items-center px-3 py-2 border border-gray-300 
                       rounded-md text-sm font-medium text-gray-700 bg-white 
                       hover:bg-gray-50 focus:outline-none focus:ring-2 
                       focus:ring-offset-2 focus:ring-blue-500"
              aria-label={copied ? "Copied to clipboard" : "Copy to clipboard"}
            >
              {copied ? (
                <>
                  <FiCheck className="w-5 h-5 text-green-500" />
                  <span className="sr-only">Copied!</span>
                </>
              ) : (
                <>
                  <FiCopy className="w-5 h-5" />
                  <span className="sr-only">Copy</span>
                </>
              )}
            </button>
          </div>

          {copied && (
            <p className="text-green-600 text-sm mt-2">
              Link copied to clipboard! ✨
            </p>
          )}

          <p className="text-gray-500 text-sm mt-4">
            This quote link will remain active for the next 30 days. Anyone with the link can view the quote details.
          </p>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 
                       hover:bg-gray-100 rounded-md"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 